import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Box,
} from '@mui/material';
import { youtubeService } from '../services/youtubeService';

interface SearchResult {
  id: string;
  title: string;
  thumbnailUrl: string;
  viewCount: string;
}

interface YouTubeSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (videoId: string) => void;
  initialQuery?: string;
}

export const YouTubeSearchDialog: React.FC<YouTubeSearchDialogProps> = ({
  open,
  onClose,
  onSelect,
  initialQuery = '',
}) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Effect to update internal search query when initialQuery prop changes
  useEffect(() => {
    setSearchQuery(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    // Debounce search slightly to avoid excessive API calls while typing
    const timerId = setTimeout(() => {
      if (open && searchQuery.trim()) {
        handleSearch();
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timerId); // Cleanup timeout on unmount or change
  }, [open, searchQuery]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const results = await youtubeService.searchVideos(searchQuery);
      setSearchResults(results.map(video => ({
        id: video.id,
        title: video.title,
        thumbnailUrl: video.thumbnailUrl,
        viewCount: video.viewCount,
      })));
    } catch (err) {
      setError('Failed to search videos. Please try again.');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoSelect = (videoId: string) => {
    console.log('[YouTubeSearchDialog] Video selected, ID:', videoId);
    onSelect(videoId);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Search YouTube Videos</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search for videos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
        />
        
        {error && (
          <Box sx={{ color: 'error.main', mb: 2 }}>
            {error}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {searchResults.map((result) => (
              <ListItem
                key={result.id}
                component="div"
                onClick={() => handleVideoSelect(result.id)}
                sx={{ cursor: 'pointer' }}
              >
                <ListItemAvatar>
                  <Avatar
                    variant="square"
                    src={result.thumbnailUrl}
                    sx={{ width: 120, height: 68, mr: 2 }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={result.title}
                  secondary={`Views: ${result.viewCount}`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}; 